import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, PhoneIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import api, { extractErrorMessage } from '../../services/api';
import { conversationAPI } from '../../services/api';
import { userAssistantConfigService } from '../../services/userAssistantConfigService';
import { buildCreateRunPayload } from '../../services/payload';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';

type CampaignLead = {
  id: string;
  name: string;
  phone: string;
  email?: string;
};

type RunCampaignModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedLeads: CampaignLead[];
  setToast: (t: { type: 'success' | 'error'; message: string } | null) => void;
};

const RunCampaignModal: React.FC<RunCampaignModalProps> = ({
  isOpen,
  onClose,
  selectedLeads,
  setToast,
}) => {
  const navigate = useNavigate();
  const [savedAgents, setSavedAgents] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [campaignNameTouched, setCampaignNameTouched] = useState(false);
  const [starting, setStarting] = useState(false);
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  // LT-380: pin agent version at launch (defaults true).
  const [pinAgentVersionAtLaunch, setPinAgentVersionAtLaunch] = useState(true);
  const agentDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!agentDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(e.target as Node)) {
        setAgentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [agentDropdownOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedAgentId('');
    setCampaignName('');
    setCampaignNameTouched(false);
    setPinAgentVersionAtLaunch(true);
    setLoadingAgents(true);
    conversationAPI
      .getSavedPrompts()
      .then((res: { agents?: Array<{ id: string; name: string }> }) => {
        const list = Array.isArray(res?.agents) ? res.agents : [];
        setSavedAgents(list.map((p) => ({ id: p.id, name: p.name })));
      })
      .catch(() => setSavedAgents([]))
      .finally(() => setLoadingAgents(false));
  }, [isOpen]);

  /** Same call-dispatch flow as CallLeadModal: create run with selected agent + all leads, start run, redirect to progress */
  const handleStartCalls = async () => {
    const trimmedCampaignName = campaignName.trim();
    if (!selectedAgentId || selectedLeads.length === 0 || !trimmedCampaignName) {
      if (!trimmedCampaignName) setCampaignNameTouched(true);
      return;
    }
    setStarting(true);
    try {
      let assistantId = '';
      const config = await userAssistantConfigService.getConfig().catch(() => null);
      if (config?.outbound_assistant_id) {
        assistantId = config.outbound_assistant_id.trim();
      } else {
        const validation = await userAssistantConfigService.validateConfig().catch(() => null);
        const outboundMissing = !validation || validation.missing?.includes('outbound_assistant_id');
        if (outboundMissing) {
          setToast({
            type: 'error',
            message: 'Outbound assistant is not configured. Please set it up in Settings.',
          });
          setStarting(false);
          return;
        }
        // Global outbound fallback exists on backend; keep payload assistant_id non-empty.
        assistantId = 'global-fallback';
      }

      const p = await conversationAPI.getSavedPrompt(selectedAgentId);
      if (!p) {
        setToast({ type: 'error', message: 'Could not load agent config.' });
        setStarting(false);
        return;
      }

      const additionalNotesEntry = Array.isArray(p.custom_fields)
        ? p.custom_fields.find((f: { title?: string }) => f?.title === 'Additional notes')
        : undefined;
      const customFieldsPayload =
        additionalNotesEntry?.value?.trim()
          ? [{ title: 'Additional notes', value: additionalNotesEntry.value.trim() }]
          : undefined;

      let defaultVoiceId: string | undefined;
      try {
        const voiceOptions = await userAssistantConfigService.getGlobalVoiceOptions();
        defaultVoiceId = voiceOptions?.global_voice_id_male ?? undefined;
      } catch {
        // use undefined
      }

      const leadsPayload = selectedLeads.map((l) => ({
        id: l.id,
        name: l.name,
        phone: l.phone,
        email: l.email || undefined,
      }));
      const voiceId = (p.voice_id && p.voice_id.trim()) ? p.voice_id : defaultVoiceId;
      const payload = buildCreateRunPayload(
        trimmedCampaignName,
        assistantId,
        leadsPayload,
        p.context ?? undefined,
        undefined,
        p.goals ?? undefined,
        p.target_audience ?? undefined,
        p.call_objective ?? undefined,
        p.success_metrics ?? undefined,
        p.agent_name ?? undefined,
        customFieldsPayload,
        voiceId,
        undefined,
        pinAgentVersionAtLaunch,
      );

      const createRes = await api.post<{ id: string }>('/runs/', payload);
      const runId = createRes.data.id;
      await api.post(`/runs/${runId}/start`);
      track(AnalyticsEvents.CAMPAIGN_CREATED, { lead_count: selectedLeads.length, run_id: runId });
      onClose();
      navigate(`/runs/${runId}/progress`);
      setToast({ type: 'success', message: 'Calls started. Redirecting to progress.' });
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to start calls');
      setToast({ type: 'error', message });
    } finally {
      setStarting(false);
    }
  };

  if (!isOpen) return null;

  const count = selectedLeads.length;

  const modalContent = (
    <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-[9999] overflow-y-auto custom-scrollbar flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <PhoneIcon className="w-6 h-6 text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">Call leads</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-sm text-gray-300">
            <strong className="text-white">{count}</strong> lead{count !== 1 ? 's' : ''} selected. Choose an agent to start calls.
          </p>
          <div>
            <label
              htmlFor="run-campaign-name-input"
              className="block text-sm font-medium text-gray-300 mb-1.5"
            >
              Campaign name *
            </label>
            <input
              id="run-campaign-name-input"
              type="text"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              onBlur={() => setCampaignNameTouched(true)}
              placeholder="Enter campaign name"
              className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            {campaignNameTouched && campaignName.trim().length === 0 && (
              <p className="mt-1 text-xs text-red-300">Campaign name is required.</p>
            )}
          </div>
          <div ref={agentDropdownRef} className="relative">
            <label
              id="run-campaign-agent-label"
              className="block text-sm font-medium text-gray-300 mb-1.5"
            >
              Select agent *
            </label>
            {loadingAgents ? (
              <p className="text-gray-400 text-sm">Loading saved agents...</p>
            ) : savedAgents.length === 0 ? (
              <p className="text-gray-400 text-sm">
                No saved agents. Create one from Campaigns or Agents, then try again.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={agentDropdownOpen}
                  aria-labelledby="run-campaign-agent-label"
                  onClick={() => setAgentDropdownOpen((prev) => !prev)}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-left text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 flex items-center justify-between gap-2"
                >
                  <span className="truncate">
                    {selectedAgentId
                      ? savedAgents.find((a) => a.id === selectedAgentId)?.name ?? 'Choose an agent'
                      : 'Choose an agent'}
                  </span>
                  <ChevronDownIcon
                    className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${agentDropdownOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {agentDropdownOpen && (
                  <ul
                    role="listbox"
                    aria-labelledby="run-campaign-agent-label"
                    className="absolute left-0 right-0 top-full mt-1 z-20 py-1 rounded-lg bg-slate-800 border border-slate-600/50 shadow-xl max-h-48 overflow-y-auto custom-scrollbar"
                  >
                    <li
                      role="option"
                      aria-selected={!selectedAgentId}
                      onClick={() => {
                        setSelectedAgentId('');
                        setAgentDropdownOpen(false);
                      }}
                      className={`px-3 py-2.5 cursor-pointer text-sm truncate ${!selectedAgentId ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}
                    >
                      Choose an agent
                    </li>
                    {savedAgents.map((agent) => (
                      <li
                        key={agent.id}
                        role="option"
                        aria-selected={selectedAgentId === agent.id}
                        onClick={() => {
                          setSelectedAgentId(agent.id);
                          setAgentDropdownOpen(false);
                        }}
                        className={`px-3 py-2.5 cursor-pointer text-sm truncate ${selectedAgentId === agent.id ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}
                      >
                        {agent.name}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          {/* LT-380: pin agent version at launch — defaults true. */}
          <div>
            <label
              htmlFor="run-campaign-pin-version"
              className="inline-flex items-start gap-2 text-sm text-gray-300 cursor-pointer select-none"
            >
              <input
                id="run-campaign-pin-version"
                type="checkbox"
                checked={pinAgentVersionAtLaunch}
                onChange={(e) => setPinAgentVersionAtLaunch(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border border-white/20 bg-white/5 accent-cyan-500"
                disabled={starting}
              />
              <span className="flex flex-col gap-0.5">
                <span>Pin agent version at launch</span>
                <span className="text-[11px] text-gray-400">
                  Default on. When off, calls use the latest agent version instead of snapshotting at launch.
                </span>
              </span>
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleStartCalls}
              disabled={
                starting || loadingAgents || !selectedAgentId || savedAgents.length === 0 || selectedLeads.length === 0 || campaignName.trim().length === 0
              }
              className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {starting ? 'Starting...' : count === 1 ? 'Start call' : 'Start calls'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default RunCampaignModal;
